<template>
  <div 
    ref="cardElement"
    class="group relative bg-white/70 dark:bg-gray-800/70 backdrop-blur-md rounded-2xl sm:rounded-3xl md:rounded-[2.5rem] overflow-hidden transition-all duration-700 hover:-translate-y-3 cursor-pointer border border-gray-300/80 dark:border-gray-700/50 shadow-xl shadow-gray-300/40 dark:shadow-black/20 flex flex-col h-full"
    :class="compact ? 'lg:rounded-[2rem]' : ''"
    :style="{ 
      '--accent-color': accentColor,
    }"
    @click="handleCardClick"
    @mouseleave="handleMouseLeave"
    @focusout="handleFocusOut"
    tabindex="0"
  >
    <!-- Dynamic Glow Effect on Hover -->
    <div 
      class="absolute -inset-px bg-gradient-to-br from-transparent via-[var(--accent-color)]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-2xl -z-10"
    ></div>

    <!-- Image Section -->
    <div
      class="relative overflow-hidden bg-white dark:bg-gray-900/50"
      :class="compact ? 'aspect-video lg:aspect-[16/8.5] xl:aspect-video' : 'aspect-video'"
    >
      <!-- Logo Display (bevorzugt wenn vorhanden) -->
      <div
        v-if="hasLogos"
        class="absolute inset-0 flex items-center justify-center transition-transform duration-700 group-hover:scale-110 z-30"
        :class="compact ? 'p-4 md:p-5 lg:p-4 xl:p-6' : 'p-4 md:p-6'"
      >
        <img 
          v-if="activeLogo && showLogo"
          ref="logoElement"
          :key="`logo-${project.title}-${resolvedTheme}-${logoReloadTrigger}`" 
          :src="logoUrlWithCacheBuster" 
          :alt="project.title" 
          class="w-auto object-contain transition-all duration-700 drop-shadow-2xl relative z-10"
          :class="[
            compact ? 'h-[80%] max-w-[80%] xl:h-[85%] xl:max-w-[85%]' : 'h-[85%] max-w-[85%]',
            { 'opacity-0': !logoLoaded && !logoError }
          ]"
          loading="lazy"
          @load="handleLogoLoad"
          @error="handleLogoError"
        />
        <!-- Loading Placeholder -->
        <div v-if="!logoLoaded && !logoError && activeLogo && showLogo" class="absolute inset-0 flex items-center justify-center z-10">
          <div class="w-12 h-12 border-2 border-blue-600/20 border-t-blue-600 rounded-full animate-spin"></div>
        </div>
      </div>

      <!-- Screenshot Gallery (nur wenn keine Logos vorhanden) -->
      <div 
        v-else-if="project.screenshots && project.screenshots.length > 0"
        class="absolute inset-0 flex transition-transform duration-1000 cubic-bezier(0.4, 0, 0.2, 1)"
        :style="{ transform: `translateX(-${currentImageIndex * 100}%)` }"
      >
        <div
          v-for="(screenshot, index) in project.screenshots"
          :key="index"
          class="min-w-full h-full relative"
        >
          <img
            :src="screenshot"
            :alt="`${project.title} - Screenshot ${index + 1}`"
            class="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
            loading="lazy"
            @error="handleImageError"
          />
        </div>
      </div>
      
      <!-- Fallback: Platzhalter wenn weder Logo noch Screenshots vorhanden -->
      <div v-else class="absolute inset-0 flex items-center justify-center bg-gray-200 dark:bg-gray-800">
        <PhotoIcon class="w-16 h-16 text-gray-400 dark:text-gray-600" />
      </div>
      
      <!-- Overlays - Gradient nur im Dark Mode für Logos, immer für Screenshots -->
      <div v-if="hasLogos" class="absolute inset-0 dark:bg-gradient-to-t dark:from-gray-900/80 dark:via-gray-900/20 dark:to-transparent dark:opacity-40 dark:group-hover:opacity-20 transition-opacity duration-700 z-10"></div>
      <div v-else class="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/20 to-transparent opacity-40 group-hover:opacity-20 transition-opacity duration-700 z-10"></div>
      
      <!-- Tech Badges (Top Left) - nur anzeigen wenn keine Logos vorhanden -->
      <div v-if="!hasLogos" class="absolute top-2 left-2 sm:top-4 sm:left-4 md:top-6 md:left-6 flex flex-wrap gap-1 sm:gap-2 z-20">
        <span
          v-for="tech in previewTechnologies"
          :key="tech"
          class="px-1.5 py-0.5 sm:px-2 sm:py-1 md:px-3 md:py-1.5 bg-gray-900/60 backdrop-blur-md rounded-lg sm:rounded-xl text-[8px] sm:text-[9px] md:text-[10px] font-bold text-white border border-white/10 uppercase tracking-widest shadow-lg"
        >
          {{ tech }}
        </span>
        <span v-if="remainingPreviewTechnologies > 0" class="px-1.5 py-0.5 sm:px-2 sm:py-1 md:px-2.5 md:py-1.5 bg-blue-600/80 backdrop-blur-md rounded-lg sm:rounded-xl text-[8px] sm:text-[9px] md:text-[10px] font-bold text-white border border-white/10 shadow-lg">
          +{{ remainingPreviewTechnologies }}
        </span>
      </div>

      <!-- Navigation Arrows (only visible on hover, nur wenn Screenshots vorhanden und keine Logos) -->
      <div 
        v-if="project.screenshots && project.screenshots.length > 1 && !hasLogos"
        class="absolute inset-x-2 sm:inset-x-4 md:inset-x-6 top-1/2 -translate-y-1/2 flex justify-between opacity-0 group-hover:opacity-100 transition-all duration-500 z-30"
      >
        <button @click.stop="previousImage" class="p-1.5 sm:p-2 md:p-2.5 bg-white/20 hover:bg-white/40 backdrop-blur-md rounded-full transition-all border border-white/20 text-white shadow-lg">
          <ChevronLeftIcon class="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" />
        </button>
        <button @click.stop="nextImage" class="p-1.5 sm:p-2 md:p-2.5 bg-white/20 hover:bg-white/40 backdrop-blur-md rounded-full transition-all border border-white/20 text-white shadow-lg">
          <ChevronRightIcon class="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" />
        </button>
      </div>
    </div>

    <!-- Content Section -->
    <div
      class="relative flex flex-col h-full"
      :class="compact ? 'p-3 sm:p-4 md:p-5 lg:p-6 xl:p-8' : 'p-3 sm:p-4 md:p-6 lg:p-8'"
    >
      <div
        class="flex items-start justify-between"
        :class="compact ? 'mb-3 sm:mb-4 md:mb-5 lg:mb-4 xl:mb-6' : 'mb-3 sm:mb-4 md:mb-6'"
      >
        <div class="flex-1">
          <h3
            class="font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors tracking-tight line-clamp-2"
            :class="compact ? 'text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl min-h-[2.5rem] sm:min-h-[3rem] lg:min-h-[3.1rem] xl:min-h-[3.5rem]' : 'text-sm sm:text-base md:text-lg lg:text-2xl min-h-[2.5rem] sm:min-h-[3rem] md:min-h-[3.5rem]'"
          >
            {{ project.title }}
          </h3>
          <!-- Accent Color Trennlinie -->
          <div class="flex justify-start py-1 sm:py-2" :class="compact ? 'lg:py-1.5' : ''">
            <div 
              class="h-0.5 transition-all duration-700 group-hover:h-1"
              :class="compact ? 'w-12 sm:w-16 md:w-20 lg:w-20 xl:w-24 group-hover:w-16 sm:group-hover:w-24 md:group-hover:w-28 lg:group-hover:w-24 xl:group-hover:w-32' : 'w-12 sm:w-16 md:w-20 lg:w-24 group-hover:w-16 sm:group-hover:w-24 md:group-hover:w-28 lg:group-hover:w-32'"
              :style="{ backgroundColor: accentColor }"
            ></div>
          </div>
        </div>
        
        <!-- Action Icon -->
        <div
          class="rounded-xl sm:rounded-2xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400 opacity-0 group-hover:opacity-100 translate-x-4 group-hover:translate-x-0 transition-all duration-500 border border-blue-100 dark:border-blue-800 flex-shrink-0 ml-2 sm:ml-3 md:ml-4"
          :class="compact ? 'w-8 h-8 sm:w-10 sm:h-10 md:w-11 md:h-11 lg:w-10 lg:h-10 xl:w-12 xl:h-12 lg:ml-3' : 'w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12'"
        >
          <ChevronRightIcon :class="compact ? 'w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5 xl:w-6 xl:h-6' : 'w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6'" />
        </div>
      </div>

      <p 
        class="text-gray-600 dark:text-gray-400 leading-relaxed font-light flex-grow"
        :class="[
          compact ? 'text-xs sm:text-sm lg:text-[13px] xl:text-sm mb-4 sm:mb-5 md:mb-6 lg:mb-5 xl:mb-8' : 'text-xs sm:text-sm mb-4 sm:mb-6 md:mb-8',
          hideDescriptionOnMobile
            ? compact
              ? 'hidden sm:line-clamp-2 xl:line-clamp-3'
              : 'hidden sm:line-clamp-3'
            : compact
              ? 'line-clamp-3 sm:line-clamp-3 xl:line-clamp-3'
              : 'line-clamp-2 sm:line-clamp-3'
        ]"
      >
        {{ project.description }}
      </p>
      
      <!-- Tags über der Trennlinie -->
      <div
        v-if="visibleTechnologies.length > 0"
        class="flex flex-wrap pt-1"
        :class="compact ? 'gap-1.5 sm:gap-2 mb-3 sm:mb-4 md:mb-5 lg:mb-4 xl:mb-6' : 'gap-2 mb-3 sm:mb-4 md:mb-6'"
      >
        <span
          v-for="tech in visibleTechnologies"
          :key="tech"
          class="px-2 py-1 sm:px-2.5 sm:py-1 md:px-3 md:py-1.5 bg-gray-100 dark:bg-gray-700/50 backdrop-blur-sm rounded-lg sm:rounded-xl text-[8px] sm:text-[9px] md:text-[10px] font-bold text-gray-700 dark:text-gray-300 border border-gray-200/50 dark:border-gray-600/50 uppercase tracking-wider"
        >
          {{ tech }}
        </span>
        <span
          v-if="remainingTechnologies > 0"
          class="px-2 py-1 sm:px-2.5 sm:py-1 md:px-3 md:py-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg sm:rounded-xl text-[8px] sm:text-[9px] md:text-[10px] font-bold text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800/50 uppercase tracking-wider"
        >
          +{{ remainingTechnologies }}
        </span>
      </div>
      
      <!-- Footer Links - immer unten -->
      <a
        v-if="project.projectPageUrl"
        :href="project.projectPageUrl"
        class="flex items-center border-t border-gray-100 dark:border-gray-700/50 mt-auto group/footer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
        :class="compact ? 'gap-2 sm:gap-4 md:gap-5 pt-3 sm:pt-4 md:pt-5 lg:pt-4 xl:pt-6' : 'gap-2 sm:gap-4 md:gap-6 pt-3 sm:pt-4 md:pt-6'"
        @click.stop
      >
        <div class="flex items-center gap-1 sm:gap-2 flex-1">
          <ChevronRightIcon class="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 text-blue-600 dark:text-blue-400 group-hover/footer:translate-x-1 transition-transform" />
          <span class="text-[9px] sm:text-[10px] md:text-[11px] font-bold text-gray-600 dark:text-gray-400 group-hover/footer:text-blue-600 dark:group-hover/footer:text-blue-400 transition-colors uppercase tracking-widest">
            Zur Detailseite
          </span>
        </div>
        <a
          v-if="project.githubUrl"
          :href="project.githubUrl"
          target="_blank"
          class="flex items-center gap-1 sm:gap-2 text-[9px] sm:text-[10px] md:text-[11px] font-bold text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors group/link uppercase tracking-widest"
          @click.stop
        >
          <CodeBracketIcon class="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 text-blue-600 dark:text-blue-400 group-hover/link:scale-110 transition-transform" />
          <span class="hidden sm:inline">Code</span>
        </a>
        <button
          v-if="project.gallery?.length"
          class="flex items-center gap-1 sm:gap-2 text-[9px] sm:text-[10px] md:text-[11px] font-bold text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors uppercase tracking-widest"
          @click.stop="isGalleryModalOpen = true"
        >
          <PhotoIcon class="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 text-blue-600 dark:text-blue-400" />
          <span class="hidden sm:inline">Galerie</span>
        </button>
      </a>
      <div
        v-else
        class="flex items-center border-t border-gray-100 dark:border-gray-700/50 mt-auto"
        :class="compact ? 'gap-2 sm:gap-4 md:gap-5 pt-3 sm:pt-4 md:pt-5 lg:pt-4 xl:pt-6' : 'gap-2 sm:gap-4 md:gap-6 pt-3 sm:pt-4 md:pt-6'"
      >
        <a
          v-if="project.githubUrl"
          :href="project.githubUrl"
          target="_blank"
          class="flex items-center gap-1 sm:gap-2 text-[9px] sm:text-[10px] md:text-[11px] font-bold text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors group/link uppercase tracking-widest"
          @click.stop
        >
          <CodeBracketIcon class="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 text-blue-600 dark:text-blue-400 group-hover/link:scale-110 transition-transform" />
          <span class="hidden sm:inline">Code</span>
        </a>
        <button
          v-if="project.gallery?.length"
          class="flex items-center gap-1 sm:gap-2 text-[9px] sm:text-[10px] md:text-[11px] font-bold text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors uppercase tracking-widest"
          @click.stop="isGalleryModalOpen = true"
        >
          <PhotoIcon class="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 text-blue-600 dark:text-blue-400" />
          <span class="hidden sm:inline">Galerie</span>
        </button>
      </div>
    </div>

    <!-- Modals -->
    <ProjectGalleryModal
      v-if="isGalleryModalOpen"
      :is-open="isGalleryModalOpen"
      :media="project.gallery || []"
      :project-title="project.title"
      :accent-color="accentColor"
      @close="isGalleryModalOpen = false"
    />

    <ProjectDetailModal
      :is-open="isDetailModalOpen"
      :project="project"
      :accent-color="accentColor"
      @close="isDetailModalOpen = false"
    />
  </div>
</template>


<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { 
  ChevronLeftIcon, 
  ChevronRightIcon, 
  CodeBracketIcon, 
  PhotoIcon 
} from '@heroicons/vue/24/outline'
import { useTheme } from '../composables/useTheme'
import ProjectGalleryModal from './ProjectGalleryModal.vue'
import ProjectDetailModal from './ProjectDetailModal.vue'
import { logger } from '../utils/logger'

interface Project {
  title: string
  description: string
  screenshots: string[]
  technologies: string[]
  liveUrl?: string
  githubUrl?: string
  projectPageUrl?: string
  logoLight?: string
  logoDark?: string
  accentColor?: string
  gallery?: { url: string; type: 'image' | 'video'; caption?: string; order: number }[]
}

interface Props {
  project: Project
  compact?: boolean
  hideDescriptionOnMobile?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  compact: false,
  hideDescriptionOnMobile: false
})
const route = useRoute()
const router = useRouter()
const { resolvedTheme } = useTheme()

const cardElement = ref<HTMLDivElement | null>(null)
const currentImageIndex = ref(0)
const isGalleryModalOpen = ref(false)
const isDetailModalOpen = ref(false)
const logoElement = ref<HTMLImageElement | null>(null)
const logoLoaded = ref(false)
const logoError = ref(false)
const logoReloadTrigger = ref(0)
const showLogo = ref(true)

const accentColor = computed(() => props.project.accentColor || '#3B82F6')
const previewTechLimit = computed(() => props.compact ? 2 : 3)
const previewTechnologies = computed(() => props.project.technologies.slice(0, previewTechLimit.value))
const remainingPreviewTechnologies = computed(() => Math.max(0, props.project.technologies.length - previewTechnologies.value.length))
const visibleTechnologies = computed(() => props.compact ? props.project.technologies.slice(0, 3) : props.project.technologies)
const remainingTechnologies = computed(() => Math.max(0, props.project.technologies.length - visibleTechnologies.value.length))

const activeLogo = computed(() => {
  // Verwende resolvedTheme direkt für bessere Synchronisation
  const theme = resolvedTheme.value
  // Im Dark Mode: logoDark bevorzugen, sonst logoLight als Fallback
  if (theme === 'dark') {
    return props.project.logoDark || props.project.logoLight || ''
  } 
  // Im Light Mode: logoLight bevorzugen, sonst logoDark als Fallback
  return props.project.logoLight || props.project.logoDark || ''
})

// URL mit Cache-Busting-Parameter für zuverlässiges Neuladen
const logoUrlWithCacheBuster = computed(() => {
  if (!activeLogo.value) return ''
  const separator = activeLogo.value.includes('?') ? '&' : '?'
  return `${activeLogo.value}${separator}_t=${logoReloadTrigger.value}`
})

// Preload-Logo basierend auf initialem Theme mit automatischer Bereinigung
const preloadLogo = (url: string) => {
  if (!url || typeof document === 'undefined') return
  
  const existingPreload = document.querySelector(`link[rel="preload"][as="image"][href="${url}"]`)
  if (existingPreload) return
  
  const link = document.createElement('link')
  link.rel = 'preload'
  link.as = 'image'
  link.href = url
  link.setAttribute('fetchpriority', 'high')
  document.head.appendChild(link)
  
  // Entferne Preload-Link, sobald das Bild geladen wurde oder nach 5 Sekunden
  const img = new Image()
  const timeout = setTimeout(() => {
    link.remove()
  }, 5000) // Entferne nach 5 Sekunden, falls nicht verwendet
  
  img.onload = () => {
    clearTimeout(timeout)
    // Entferne Preload-Link, sobald Bild geladen wurde
    link.remove()
  }
  img.onerror = () => {
    clearTimeout(timeout)
    link.remove()
  }
  img.src = url
}

const hasLogos = computed(() => !!(props.project.logoLight || props.project.logoDark))

const handleCardClick = () => {
  // Focus zurücksetzen, um sicherzustellen, dass keine Focus-States aktiv bleiben
  if (cardElement.value) {
    cardElement.value.blur()
  }
  
  // Wenn Projekt eine Detailseite hat, direkt zur Detailseite navigieren
  if (props.project.projectPageUrl) {
    router.push(props.project.projectPageUrl)
  } else {
    // Ansonsten Modal öffnen
    isDetailModalOpen.value = true
  }
}

const handleMouseLeave = () => {
  // Stelle sicher, dass Hover-States zurückgesetzt werden
  if (cardElement.value) {
    cardElement.value.blur()
  }
}

const handleFocusOut = () => {
  // Stelle sicher, dass Focus-States zurückgesetzt werden
  // Kleine Verzögerung, um sicherzustellen, dass der Focus wirklich weg ist
  setTimeout(() => {
    if (cardElement.value && document.activeElement !== cardElement.value) {
      // Nur zurücksetzen, wenn das Element nicht mehr fokussiert ist
    }
  }, 0)
}
let autoSlideInterval: number | null = null

const nextImage = () => {
  currentImageIndex.value = (currentImageIndex.value + 1) % props.project.screenshots.length
}

const previousImage = () => {
  currentImageIndex.value = currentImageIndex.value === 0 
    ? props.project.screenshots.length - 1 
    : currentImageIndex.value - 1
}

const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  img.src = 'https://via.placeholder.com/800x600/1f2937/ffffff?text=Screenshot+Placeholder'
}

const handleLogoLoad = () => {
  logoLoaded.value = true
  logoError.value = false
}

const handleLogoError = (event: Event) => {
  const img = event.target as HTMLImageElement
  const currentSrc = img.src
  logoError.value = true
  
  // Versuche Fallback-Logo basierend auf aktuellem Theme
  const theme = resolvedTheme.value
  if (theme === 'dark' && props.project.logoLight && currentSrc === props.project.logoDark) {
    img.src = props.project.logoLight
    logoError.value = false
  } else if (theme === 'light' && props.project.logoDark && currentSrc === props.project.logoLight) {
    img.src = props.project.logoDark
    logoError.value = false
  } else {
    logger.warn(`Logo konnte nicht geladen werden: ${currentSrc}`)
  }
}

const startAutoSlide = () => {
  if (props.project.screenshots.length > 1) {
    autoSlideInterval = window.setInterval(() => {
      nextImage()
    }, 5000)
  }
}

const stopAutoSlide = () => {
  if (autoSlideInterval) {
    clearInterval(autoSlideInterval)
    autoSlideInterval = null
  }
}

// Funktion zum Neuladen des Logos
const reloadLogo = (logoUrl: string) => {
  if (!logoUrl) return
  
  // Setze States zurück
  logoLoaded.value = false
  logoError.value = false
  
  // Entferne das Bild-Element temporär, um sicherzustellen, dass es neu gerendert wird
  showLogo.value = false
  
  // Inkrementiere reloadTrigger für Cache-Busting
  logoReloadTrigger.value++
  
  // Preload neues Logo für schnelleren Wechsel
  preloadLogo(logoUrl)
  
  // Stelle das Bild-Element nach einem kurzen Delay wieder her
  // Dies zwingt Vue, das img-Element komplett neu zu erstellen
  setTimeout(() => {
    nextTick(() => {
      showLogo.value = true
      
      // Prüfe nach dem nächsten Render, ob das Bild bereits geladen ist (z.B. aus dem Cache)
      nextTick(() => {
        requestAnimationFrame(() => {
          nextTick(() => {
            if (logoElement.value && logoElement.value.complete && logoElement.value.naturalHeight !== 0) {
              handleLogoLoad()
            }
          })
        })
      })
    })
  }, 10)
}

// Watch für resolvedTheme - wird getriggert wenn Theme wechselt
watch(resolvedTheme, (newTheme, oldTheme) => {
  // Nur reagieren wenn sich das Theme tatsächlich geändert hat
  if (newTheme !== oldTheme && hasLogos.value && activeLogo.value) {
    // Lade das neue Logo
    nextTick(() => {
      reloadLogo(activeLogo.value)
    })
  }
}, { immediate: false })

// Watch für activeLogo - wird getriggert wenn sich das Logo ändert (z.B. durch Theme-Wechsel oder Projekt-Update)
watch(activeLogo, (newLogo, oldLogo) => {
  // Nur reagieren wenn sich das Logo tatsächlich geändert hat
  if (newLogo && newLogo !== oldLogo && hasLogos.value) {
    // Lade das neue Logo
    nextTick(() => {
      reloadLogo(newLogo)
    })
  }
}, { immediate: false })

// Reset Focus-States wenn man von einer Projektseite zurückkommt
watch(() => route.path, (newPath, oldPath) => {
  // Wenn man von einer Projektseite zurückkommt (z.B. /projects/... -> / oder /portfolio)
  if (oldPath?.startsWith('/projects/') && !newPath.startsWith('/projects/')) {
    // Kleine Verzögerung, um sicherzustellen, dass die Navigation abgeschlossen ist
    nextTick(() => {
      if (cardElement.value) {
        cardElement.value.blur()
        // Stelle sicher, dass keine Hover-States aktiv bleiben
        const activeElement = document.activeElement as HTMLElement
        if (activeElement && activeElement !== cardElement.value) {
          activeElement.blur()
        }
      }
    })
  }
}, { immediate: false })

onMounted(() => {
  startAutoSlide()
  
  // Preload Logo beim ersten Mount basierend auf initialem Theme
  if (hasLogos.value && activeLogo.value) {
    preloadLogo(activeLogo.value)
    
    // Preload auch das alternative Logo für schnelleren Theme-Wechsel
    const theme = resolvedTheme.value
    if (theme === 'dark' && props.project.logoLight) {
      preloadLogo(props.project.logoLight)
    } else if (theme === 'light' && props.project.logoDark) {
      preloadLogo(props.project.logoDark)
    }
    
    // Prüfe, ob das Bild bereits geladen ist (z.B. aus dem Cache)
    nextTick(() => {
      if (logoElement.value && logoElement.value.complete && logoElement.value.naturalHeight !== 0) {
        handleLogoLoad()
      }
    })
  }
  
  // Stelle sicher, dass beim Mount keine Focus-States aktiv sind
  if (cardElement.value) {
    cardElement.value.blur()
  }
})

onUnmounted(() => {
  stopAutoSlide()
})
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Service Card Shimmer Effect */
.group:hover .shimmer {
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%) skewX(-15deg);
  }
  100% {
    transform: translateX(200%) skewX(-15deg);
  }
}
</style>
